import { Dispatch, createSlice } from '@reduxjs/toolkit'
import { DateRange } from '../../types'
import { startOfMinute, subHours } from 'date-fns'
import TimePeriod from '../../enums/TimePeriod'

interface QueryState {
  dateRange: DateRange
  timePeriod: TimePeriod

  /**
   * Indicates if tracking the current time automatically.
   */
  autoRefresh: boolean
}

const initialState: QueryState | undefined = getInitialState()

const querySlice = createSlice({
  name: 'query',
  initialState,
  reducers: {
    setDateRangeState(state, action) {
      state.dateRange = action.payload
    },
    setTimePeriodState(state, action) {
      state.timePeriod = action.payload
    },
    setAutoRefresh(state, action) {
      state.autoRefresh = action.payload
    }
  }
})

function getInitialState() {
  const searchParams = new URLSearchParams(window.location.search)
  const foundDateRange = searchParams.get('daterange') ?? undefined
  const foundTimePeriod = searchParams.get('timeperiod') ?? undefined

  if (foundDateRange) {
    const dateRangeFromQuery = JSON.parse(foundDateRange)

    const dateRange = {
      from: startOfMinute(new Date(dateRangeFromQuery.from)),
      to: startOfMinute(new Date(dateRangeFromQuery.to))
    }
    const timePeriod = (foundTimePeriod as TimePeriod) ?? TimePeriod.CUSTOM

    const relativeTimePeriods = [
      TimePeriod.ONE_HOUR,
      TimePeriod.TWENTYFOUR_HOURS,
      TimePeriod.THREE_DAYS,
      TimePeriod.SEVEN_DAYS,
      TimePeriod.THIRTY_DAYS
    ]

    const autoRefresh = relativeTimePeriods.includes(timePeriod)

    return {
      dateRange,
      timePeriod,
      autoRefresh
    }
  }

  const now = startOfMinute(new Date())

  const dateRange: DateRange = {
    from: subHours(now, 24),
    to: now
  }
  const foundHash: string = location.hash.substr(1)
  const hash = foundHash ? '#' + foundHash : ''
  searchParams.set('daterange', JSON.stringify(dateRange))

  const newRelativePathQuery = window.location.pathname + hash + '?' + searchParams.toString()

  window.history.replaceState(null, '', newRelativePathQuery)

  return {
    dateRange,
    timePeriod: TimePeriod.TWENTYFOUR_HOURS,
    autoRefresh: true
  }
}

export function getInitialTimePeriod() {
  return async (dispatch: Dispatch) => {
    const searchParams = new URLSearchParams(window.location.search)
    const foundTimePeriod = searchParams.get('timeperiod') ?? undefined
    if (foundTimePeriod) {
      dispatch(querySlice.actions.setTimePeriodState(foundTimePeriod))
    } else {
      searchParams.set('timeperiod', TimePeriod.TWENTYFOUR_HOURS)
      const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString()
      window.history.replaceState(null, '', newRelativePathQuery)
    }
  }
}

export function setDateRange(dateRange: DateRange) {
  return async (dispatch: Dispatch) => {
    const searchParams = new URLSearchParams(window.location.search)
    dispatch(querySlice.actions.setDateRangeState(dateRange))
    searchParams.set(
      'daterange',
      JSON.stringify({
        from: dateRange.from,
        to: dateRange.to
      })
    )
    const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString()
    window.history.replaceState(null, '', newRelativePathQuery)
  }
}

export function setTimePeriod(timePeriod: TimePeriod) {
  return async (dispatch: Dispatch) => {
    const searchParams = new URLSearchParams(window.location.search)
    dispatch(querySlice.actions.setTimePeriodState(timePeriod))

    // Disable auto-refresh for custom and absolute time periods.
    if (
      timePeriod === TimePeriod.CUSTOM ||
      timePeriod === TimePeriod.DAY ||
      timePeriod === TimePeriod.WEEK ||
      timePeriod === TimePeriod.MONTH
    ) {
      dispatch(querySlice.actions.setAutoRefresh(false))
    }

    searchParams.set('timeperiod', timePeriod)
    const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString()
    window.history.replaceState(null, '', newRelativePathQuery)
  }
}

export function setAutoRefresh(autoRefresh: boolean) {
  return async (dispatch: Dispatch) => {
    dispatch(querySlice.actions.setAutoRefresh(autoRefresh))

    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('autorefresh', JSON.stringify(autoRefresh))

    const newState = `${window.location.pathname}?${searchParams.toString()}`
    window.history.replaceState(null, '', newState)
  }
}

export default querySlice.reducer
